import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { quoteApi } from "src/Api/quoteApi";
import { redeemerApi } from "src/Api/redeemers";
import { LoadingStage } from "src/Interfaces/shared";
import { RootState } from ".";
import _ from "lodash";
import {
    IAddItemToExistingQuote,
    IAddQuoteCustomers,
    IQuote,
    IQuoteCustomers,
    IQuotesState,
    IUpdateQuoteCustomers,
    IAddItemToNewQuote,
    IUpdateCartItemData,
    ICartItemData,
    ISharedQuote,
} from "@interfaces";
import { RemovePayloadCart } from "@utilities";
import { updateItems } from "src/Module/Checkout/checkoutHelpers";
import { ErrorToast } from "src/Components/toasts/ErrorToast";

const initialState: IQuotesState = {
    quotes: [],
    allQuotes: [],
    loadingStage: LoadingStage.NOT_INITIALIZED,
    quotesDetails: [],
    quotesPreview: null,
    quotesCustomers: {},
    sharedQuotes: [],
};

export const loadQuotes = createAsyncThunk<IQuote[]>(
    "quotes/loadQuotes",
    async (_, { rejectWithValue }) => {
        try {
            const response = await quoteApi.getQuotes();
            return response.data.data;
        } catch (error) {
            console.error("Failed to load quotes:", error);
            return rejectWithValue("Failed to load quotes");
        }
    },
);

export const loadAllQuotes = createAsyncThunk<IQuote[]>(
    "quotes/loadAllQuotes",
    async (_, { rejectWithValue }) => {
        try {
            const response = await quoteApi.getAllQuotes();
            return response.data.data;
        } catch (error) {
            console.error("Failed to load quotes:", error);
            return rejectWithValue("Failed to load quotes");
        }
    },
);

export const loadQuoteDetails = createAsyncThunk<ICartItemData[], string>(
    "quotes/loadQuoteDetails",
    async (quoteId, { rejectWithValue }) => {
        try {
            const fetchQuote = async (): Promise<ICartItemData[]> => {
                const getDetailsResponse = await quoteApi.getQuoteDetails(quoteId);
                return getDetailsResponse.data.data;
            };

            const items = await fetchQuote();
            const allLatest = items.length > 0 && items.every((it) => !!it.is_product_latest);
            if (!allLatest && items.length > 0) {
                await quoteApi.updateCartItemsForQuote(quoteId);
                return await fetchQuote();
            }
            return items;
        } catch (error) {
            console.error(`Failed to load quote details ${quoteId}:`, error);
            return rejectWithValue("Failed to load quote details");
        }
    },
);

export const loadQuotePreview = createAsyncThunk<IQuote, string>(
    "quotes/loadQuotePreview",
    async (quoteId, { rejectWithValue }) => {
        try {
            const response = await quoteApi.getQuotePreview(quoteId);
            return response.data.data;
        } catch (error) {
            console.error(`Failed to load quote preview ${quoteId}:`, error);
            return rejectWithValue("Failed to load quote preview");
        }
    },
);

export const loadSharedQuotes = createAsyncThunk<ISharedQuote[]>(
    "quotes/loadSharedQuotes",
    async (_, { rejectWithValue }) => {
        try {
            const response = await quoteApi.getQuotesSharedToMe();
            return response.data.data || [];
        } catch (error) {
            console.error("Failed to load shared quotes:", error);
            return rejectWithValue("Failed to load shared quotes");
        }
    },
);

export const loadQuoteCustomers = createAsyncThunk<IQuoteCustomers, string>(
    "quotes/loadQuoteCustomers",
    async (quoteId, { rejectWithValue, getState }) => {
        try {
            const state = getState() as RootState;
            const isAuthenticated = !_.isNil(state.auth.jwt);

            // For authenticated users, use user endpoint to get all redeemers
            if (isAuthenticated) {
                const response = await redeemerApi.getUserRedeemers();
                return {
                    quoteId,
                    customers: response.data.data,
                };
            }

            // Fallback to quote-specific customers
            const getCustomersResponse = await quoteApi.getCustomers(quoteId);
            return {
                quoteId,
                customers: getCustomersResponse.data.data,
            };
        } catch (error) {
            console.error(`Failed to load quote customers ${quoteId}:`, error);
            return rejectWithValue("Failed to load quote customers");
        }
    },
);
export const addQuoteCustomersAndReload = createAsyncThunk<IQuoteCustomers, IAddQuoteCustomers>(
    "quotes/addQuoteCustomersAndReload",
    async (loadQuoteCustomers, { rejectWithValue }) => {
        try {
            await quoteApi.addCustomers(loadQuoteCustomers.quoteId, loadQuoteCustomers.customers);
            const getCustomersResponse = await quoteApi.getCustomers(loadQuoteCustomers.quoteId);
            return {
                quoteId: loadQuoteCustomers.quoteId,
                customers: getCustomersResponse.data.data,
            };
        } catch (error: any) {
            console.error(error);
            return rejectWithValue(error.response?.data || error.message);
        }
    },
);

export const updateQuoteCustomersAndReload = createAsyncThunk<
    IQuoteCustomers,
    IUpdateQuoteCustomers
>("quotes/updateQuoteCustomersAndReload", async (loadQuoteCustomers, { rejectWithValue }) => {
    try {
        await quoteApi.setCustomers(loadQuoteCustomers.quoteId, loadQuoteCustomers.customers);
        const getCustomersResponse = await quoteApi.getCustomers(loadQuoteCustomers.quoteId);
        return {
            quoteId: loadQuoteCustomers.quoteId,
            customers: getCustomersResponse.data.data,
        };
    } catch (error) {
        console.error(error);
        return rejectWithValue(`Failed to update qoute customers`);
    }
});

// Async thunk for adding a quote and fetching updated quotes
export const createNewQuoteAndReload = createAsyncThunk<
    IQuote[],
    IAddItemToNewQuote,
    { state: RootState }
>("quotes/createNewQuoteAndReload", async (newQuoteItem, { rejectWithValue, getState }) => {
    try {
        const state = getState() as RootState;
        const isOrderTabEnabled = Boolean(state.account.metadata?.is_order_tab_enabled);

        // Pick the correct source of "existing quotes" depending on the mode.
        const existingQuotes =
            (isOrderTabEnabled
                ? // when order mode is enabled, your slice likely stores full list under allQuotes.allQuotes
                  (state.allQuotes.allQuotes ?? state.allQuotes.quotes)
                : // when order mode is disabled, use the simple quotes array
                  state.allQuotes.quotes) ?? [];

        const isDuplicate = existingQuotes.some(
            (quote) => quote.title.toLowerCase() === newQuoteItem.quoteTitle.toLowerCase(),
        );

        if (isDuplicate) {
            ErrorToast({ Message: `Quote "${newQuoteItem.quoteTitle}" already exists` });
            return rejectWithValue(`Quote "${newQuoteItem.quoteTitle}" already exists`);
        }

        // Create the quote on server
        await quoteApi.newQuote(newQuoteItem);

        // Fetch the correct list based on order-tab flag
        let response;
        if (isOrderTabEnabled) {
            response = await quoteApi.getAllQuotes();
        } else {
            response = await quoteApi.getQuotes();
        }

        return response?.data?.data ?? [];
    } catch (error: any) {
        console.error("Failed to add item to cart or fetch updated cart:", error);
        const payload = error?.response?.data ?? {
            message: error?.message ?? "Unable to add item to cart and fetch updated cart data.",
        };
        return rejectWithValue(payload);
    }
});

// Async thunk for adding an item to an existing quote
export const addItemToExistingQuoteAndReload = createAsyncThunk<
    IQuote[],
    IAddItemToExistingQuote,
    { state: RootState }
>("quotes/addItemToExistingQuoteAndReload", async (addQuoteItem, { rejectWithValue, getState }) => {
    try {
        // send update to server
        await quoteApi.addQuoteItem(addQuoteItem.quoteId, addQuoteItem);

        const state = getState() as RootState;
        const isOrderTabEnabled = Boolean(state.account.metadata?.is_order_tab_enabled);

        // Fetch the correct list based on order-tab flag (same approach as createNewQuoteAndReload)
        let response;
        if (isOrderTabEnabled) {
            response = await quoteApi.getAllQuotes();
        } else {
            response = await quoteApi.getQuotes();
        }

        return response?.data?.data ?? [];
    } catch (error: any) {
        const payload = error?.response?.data ?? {
            message: error?.message ?? "Unable to add item to existing quote.",
        };
        console.error("Error adding item to existing quote:", error);
        return rejectWithValue(payload);
    }
});

export const deleteQuoteAndReload = createAsyncThunk<IQuote[], string, { state: RootState }>(
    "quotes/deleteQuoteAndReload",
    async (quoteId, { rejectWithValue, getState }) => {
        try {
            // delete on server
            await quoteApi.deleteQuote(quoteId);

            const state = getState() as RootState;
            const isOrderTabEnabled = Boolean(state.account.metadata?.is_order_tab_enabled);

            // Fetch the correct list based on order-tab flag (same approach as others)
            let response;
            if (isOrderTabEnabled) {
                response = await quoteApi.getAllQuotes();
            } else {
                response = await quoteApi.getQuotes();
            }

            return response?.data?.data ?? [];
        } catch (error: any) {
            console.error(`Error deleting quote ${quoteId}:`, error);
            const payload = error?.response?.data ?? {
                message: error?.message ?? "Failed to delete quote",
            };
            return rejectWithValue(payload);
        }
    },
);

export const submitQuoteItemsAndFetch = createAsyncThunk<
    ICartItemData[],
    { quoteId: string; items: IUpdateCartItemData[] }
>("quotes/submitQuoteItemsAndFetch", async (itemsBookingData, { rejectWithValue }) => {
    try {
        const responseSubmit = await updateItems(
            itemsBookingData.items,
            "quote",
            itemsBookingData.quoteId,
        );
        if (responseSubmit.success === true && responseSubmit.code === 200) {
            const response = await quoteApi.getQuoteDetails(itemsBookingData.quoteId);
            return response.data.data;
        } else {
            throw new Error("Quotes update failed");
        }
    } catch (error: any) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const removeQuoteItemAndFetch = createAsyncThunk<
    ICartItemData[],
    { quoteId: string | number } & RemovePayloadCart
>("quotes/removeQuoteItemAndFetch", async ({ quoteId, type, id }, { rejectWithValue }) => {
    try {
        await quoteApi.deleteQuoteItem({ type, id }, quoteId);
        const response = await quoteApi.getQuoteDetails(quoteId);
        return response.data.data;
    } catch (error) {
        console.error("Failed to remove item from quote:", error);
        return rejectWithValue("Unable to remove item from quote and fetch updated quote data.");
    }
});

const quotesSlice = createSlice({
    name: "quote",
    initialState,
    reducers: {
        resetQuote: () => initialState, // Reset state action
        loadQuoteData: (state, action: PayloadAction<string>) => {
            const existingQuote = state.quotes.find((q) => q.id === action.payload);
            if (!_.isNil(existingQuote)) {
                // load from cached list
                state.quotesDetails = existingQuote.items;
            }
            const existingQuoteCustomers = state.quotesCustomers[action.payload];
            if (!_.isNil(existingQuoteCustomers)) {
                // load from cached list
                state.quotesCustomers[action.payload] = existingQuoteCustomers;
            }

            loadQuoteDetails(action.payload);
            loadQuoteCustomers(action.payload);
        },
    },
    extraReducers: (builder) => {
        builder
            // Handle fetchQuote actions
            .addCase(loadQuotes.pending, (state) => {
                state.loadingStage = LoadingStage.IS_LOADING;
            })
            .addCase(loadQuotes.fulfilled, (state, action) => {
                state.quotes = action.payload;
                state.loadingStage = LoadingStage.LOADING_COMPLETE;
                // recalculateTotals(state);
            })
            .addCase(loadQuotes.rejected, (state, action) => {
                state.loadingStage = LoadingStage.LOADING_COMPLETE;
                console.error("Error fetching fetchQuote:", action.payload);
            })
            .addCase(loadAllQuotes.pending, (state) => {
                state.loadingStage = LoadingStage.IS_LOADING;
            })
            .addCase(loadAllQuotes.fulfilled, (state, action) => {
                state.allQuotes = action.payload;
                state.loadingStage = LoadingStage.LOADING_COMPLETE;
                // recalculateTotals(state);
            })
            .addCase(loadAllQuotes.rejected, (state, action) => {
                state.loadingStage = LoadingStage.LOADING_COMPLETE;
                console.error("Error fetching fetchQuote:", action.payload);
            })
            // Handle addToQuoteAndFetch actions
            .addCase(createNewQuoteAndReload.rejected, (_, action) => {
                console.error("Error adding to quote:", action.payload);
            })
            // Handle addItemToExistingQuote actions
            .addCase(addItemToExistingQuoteAndReload.rejected, (_, action) => {
                console.error("Error adding item to existing quote:", action.payload);
            })
            .addCase(loadQuoteDetails.fulfilled, (state, action) => {
                state.quotesDetails = action.payload;
            })
            .addCase(loadQuoteDetails.rejected, (_, action) => {
                console.error("Error loading quote details:", action.payload);
            })
            .addCase(loadQuotePreview.fulfilled, (state, action) => {
                state.quotesPreview = action.payload;
            })
            .addCase(loadQuotePreview.rejected, (_, action) => {
                console.error("Error loading quote preview:", action.payload);
            })
            .addCase(loadQuoteCustomers.fulfilled, (state, action) => {
                state.quotesCustomers[action.payload.quoteId] = action.payload;
            })
            .addCase(loadQuoteCustomers.rejected, (_, action) => {
                console.error("Error loading quote customers:", action.payload);
            })
            .addCase(submitQuoteItemsAndFetch.rejected, (_, action) => {
                console.error("Failed to submit cart data and fetch", action.payload);
            })
            .addCase(submitQuoteItemsAndFetch.fulfilled, (state, action) => {
                state.quotesDetails = action.payload;
            })
            .addCase(removeQuoteItemAndFetch.fulfilled, (state, action) => {
                state.quotesDetails = action.payload;
            })
            .addCase(updateQuoteCustomersAndReload.fulfilled, (state, action) => {
                state.quotesCustomers[action.payload.quoteId] = action.payload;
            })
            .addCase(addQuoteCustomersAndReload.fulfilled, (state, action) => {
                state.quotesCustomers[action.payload.quoteId] = action.payload;
            })
            .addCase(loadSharedQuotes.fulfilled, (state, action) => {
                state.sharedQuotes = action.payload;
            })
            .addCase(loadSharedQuotes.rejected, (_, action) => {
                console.error("Error loading shared quotes:", action.payload);
            });
    },
});
export const selectQuotes = (state: RootState) => state.allQuotes;

export const { resetQuote, loadQuoteData } = quotesSlice.actions;
export default quotesSlice.reducer;
